/**
 * /pricing — dedicated pricing page (D4.2 audit action).
 * Reuses the shared PricingBlock component that also renders on `/#pricing`.
 */
import { Link } from "react-router-dom";
import PricingBlock from "../components/PricingBlock";
import { usePageSeo } from "../lib/seo";

export default function Pricing() {
  usePageSeo({
    title: "iKEY Pricing — Free Forever, DocuSign Included.",
    description: "Free forever for your first 3 clients. Growth $39.99, Master $79.99, Pro $119.99. DocuSign included on every paid plan. Save 18% billed annually.",
    canonical: "https://ikey-app.com/pricing",
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="pricing-page">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-slate-200" />
            <span className="font-heading text-lg font-bold tracking-tight">iKEY</span>
          </Link>
          <div className="flex items-center gap-6 text-sm font-semibold text-slate-600">
            <Link to="/about" className="hover:text-slate-900">About</Link>
            <Link to="/vs" className="hover:text-slate-900">Compare</Link>
            <Link to="/auth?mode=login" className="hover:text-slate-900">Log in</Link>
            <Link to="/auth" className="rounded-md px-4 py-2 text-white" style={{ background: "var(--primary-hex)" }}>Get started free</Link>
          </div>
        </div>
      </header>

      <main className="py-16 lg:py-24">
        {/* Page-level H1 — required by audit for keyword targeting.
            PricingBlock renders below with `withHeading={false}` so
            there's no duplicate H2 heading immediately underneath. */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>Pricing</div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900" data-testid="pricing-h1">
            iKEY Pricing — Free Forever, DocuSign Included.
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Free forever for your first 3 clients. Upgrade when you're ready — every paid tier includes DocuSign.
          </p>
          <p className="mt-4 text-[13px] text-slate-700 font-semibold" data-testid="pricing-value-anchor">
            CRM + DocuSign + client portal + transaction tracker. <span className="text-slate-500 font-normal">One subscription. Less than the cost of DocuSign alone.</span>
          </p>
        </div>

        <div className="mt-4">
          <PricingBlock withHeading={false} />
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} iKEY, Inc. · <Link to="/terms" className="hover:text-slate-900">Terms</Link> · <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
